import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { fetchGroups, removeGroup, updateGroup } from './Groups.actions';
import {
  setSearch,
  addGroups,
  setActiveGroup,
  setListGroups,
} from './Groups.slice';
import Button from '../../components/Button/Button.component';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorPrim,
  fontColorSecond,
  red,
} from '../../assets/styledAssets/vars';
import {
  DeleteIcon,
  PlusIcon,
  SearchIcon,
} from '../../components/Icons/Icons.component';
import { StyledFormField } from '../../components/FormField/styles';
import { Input } from '@progress/kendo-react-inputs';
import { GridColumn } from '@progress/kendo-react-grid';
import GroupDetails from './components/GroupDetails/GroupDetails.component';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import debounce from 'lodash/debounce';
import { uniq } from 'lodash';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import AddEditGroup from './components/AddEditGroup/AddEditGroup.component';
import { Group } from './groups';
import { createGroup, fetchListMembers } from '../../service/Groups.service';
import AccessCode from './components/AccessCode/AccessCode.component';
import SendQuestionnaires from './components/SendQuestionnaires/SendQuestionnaires.component';
import { createGroupCode } from '../../service/Integration.service';
import { Questionnaire } from '../Questionnaires/questionnaires';
import { sendQuestionnaire } from '../../service/Questionnarie.service';
import { Code, ListResponse, User } from '../../domains/app';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';
import { ConfirmationBtnText } from '../../app/constants';
import { SlidingRight } from './styles';

const connector = connect(
  (state: RootState) => ({
    selectedAccount: state.app.selectedAccount,
    profile: state.app.user,
    listGroups: state.groups.listGroups,
    search: state.groups.search,
    isFetching: state.groups.isFetching,
    total: state.groups.total,
    pageSize: state.groups.pageSize,
    activeGroup: state.groups.activeGroup,
    currentPlan: state.settings.currentPlan,
  }),
  {
    fetchGroups,
    setSearch,
    addGroups,
    removeGroup,
    updateGroup,
    setActiveGroup,
    setListGroups,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Groups: FunctionComponent<PropsFromRedux> = ({
  selectedAccount,
  listGroups,
  search,
  isFetching,
  total,
  pageSize,
  activeGroup,
  fetchGroups,
  setSearch,
  addGroups,
  removeGroup,
  updateGroup,
  setActiveGroup,
  profile,
  setListGroups,
  currentPlan,
}) => {
  const intl = useIntl();

  const [skip, setSkip] = useState<number>(0);
  const [editedGroup, setEditedGroup] = useState<Group | undefined>();
  const [showGroupDetails, setShowGroupDetails] = useState<boolean>(false);
  const [showSend, setShowSend] = useState<boolean>(false);
  const [accessCodeDialog, setAccessCodeDialog] = useState(false);
  const [listCodes, setListCodes] = useState<Code[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    fetchGroups(true);
    return () => {
      setActiveGroup(undefined);
    };
    // eslint-disable-next-line
  }, []);

  const fetch = useMemo(
    () =>
      debounce(() => {
        fetchGroups(true);
      }, 200),
    // eslint-disable-next-line
    [],
  );

  const handleChangeInput = (e: any) => {
    setSearch(e.target.value);
    fetch();
  };

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= listGroups.length &&
        !isFetching &&
        total > listGroups.length
      ) {
        fetchGroups(false);
      }
    },
    [
      isFetching,
      listGroups.length,
      total,
      fetchGroups,
      setSkip,
      skip,
      pageSize,
    ],
  );

  const handleAddEditGroup = useCallback(
    (group: Partial<Group>) => {
      if (editedGroup?.id) {
        updateGroup(group);
        setShowGroupDetails(false);
      } else {
        createGroup(selectedAccount?.id as string, group as Group).then(
          (res) => {
            addGroups([res]);
            setShowGroupDetails(false);
          },
        );
      }
    },
    // eslint-disable-next-line
    [editedGroup],
  );

  const updateField = (dataItem: any, field: keyof Group) => {
    const index = listGroups.findIndex((m) => m.id === dataItem.id);
    let newList = [...listGroups];
    newList[index] = {
      ...newList[index],
      [field]: !dataItem[field],
    };
    setListGroups(newList);
  };

  const selectedGroups = useMemo(() => listGroups.filter((g) => g.checked), [
    listGroups,
  ]);

  const generateCode = useCallback(
    (m: number) => {
      const listIds = selectedGroups.map((g) => g.id) as string[];
      createGroupCode(activeGroup?.spaceId as string, listIds, m).then(
        (code) => {
          const now = new Date();
          now.setMinutes(now.getMinutes() + m);
          setListCodes([...listCodes, { id: code, expired: now }]);
        },
      );
    },
    [activeGroup, selectedGroups, listCodes],
  );

  const sendQuestionnaires = (questionnaires: Questionnaire[]) => {
    const listMembersRequests: Promise<ListResponse<User>>[] = [];
    selectedGroups.forEach((g) => {
      listMembersRequests.push(fetchListMembers(g.id as string, 10000, 0));
    });
    Promise.all(listMembersRequests).then((res) => {
      const selectedMemberIds = res.flatMap((uResponse) =>
        uResponse.items.map((u) => u.id),
      ) as string[];
      const listRequests: Promise<Questionnaire>[] = [];
      questionnaires.forEach((q) => {
        listRequests.push(
          sendQuestionnaire(
            q?.id as string,
            activeGroup?.spaceId as string,
            profile?.id as string,
            uniq(selectedMemberIds),
          ),
        );
      });
      Promise.all(listRequests).then(() => {
        setShowSend(false);
      });
    });
  };

  return (
    <Wrap
      flexDir={'column'}
      grow={'1'}
      overflow={['auto', 'unset']}
      margin={'-5rem -3rem 0 -3rem'}
      width={'calc(100% + 6rem)'}
      maxWidth={'none'}
      maxHeight={'none'}
    >
      <Wrap width={'100%'} height={'100%'}>
        <Wrap flexDir={'column'} width={'100%'} maxWidth={['100%', activeGroup ? 'calc(100% - 40vw)' : '100%']} height={'100%'}>
          <Wrap
            flexDir={'column'}
            grow={'1'}
            flexWrap={'nowrap'}
            padding={'5rem 3rem 0 3rem'}
            width={'100%'}
            maxHeight={'100%'}
          >
            <Wrap
              alignItems={['center', 'flex-end']}
              justifyCont={'space-between'}
              width={'100%'}
            >
              <Wrap alignItems={['flex-start', 'center']}
                    flexDir={['column', 'row']}>
                <Wrap
                  fs={'3.2rem'}
                  fw={'bold'}
                  color={'#000'}
                  alignItems={'flex-end'}
                  borders={[
                    [{ side: 'right', color: 'transparent' }],
                    [{ side: 'right', color: accentColorSecond }],
                  ]}
                  padding={['0', '0 2rem 0 0']}
                  margin={['0 0 1rem 0', '0 2rem 0 0']}
                >
                  {intl.formatMessage(messages.title)}
                </Wrap>
                <Wrap
                  fs={'1.6rem'}
                  color={fontColorSecond}
                  alignItems={'flex-end'}
                >
                  {total} {intl.formatMessage(messages.groupsTotal)}
                </Wrap>
              </Wrap>
              <Wrap justifyCont={'flex-end'}>
                <Button
                  secondary
                  onClick={() => {
                    if (
                      currentPlan &&
                      currentPlan.subscriptionPlan.maxGroupCount <= total
                    ) {
                      setShowConfirmation(true);
                    } else {
                      setEditedGroup(undefined);
                      setShowGroupDetails(true);
                    }
                  }}
                >
                  <PlusIcon color={accentColorPrim} width={'1.4rem'} />
                  {intl.formatMessage(messages.addGroup)}
                </Button>
              </Wrap>
            </Wrap>
            <Wrap
              width={'100%'}
              justifyCont={'space-between'}
              alignItems={'flex-end'}
            >
              <Wrap width={'30rem'} margin={['3rem 0', '3rem 0 0 0']}>
                <StyledFormField>
                  <Input value={search} onChange={handleChangeInput} />
                  <SearchIcon color={fontColorPrim} width={'2rem'} />
                </StyledFormField>
              </Wrap>
              {!!selectedGroups.length && (
                <Wrap alignItems={'center'}>
                  {currentPlan?.subscriptionPlan.questionnaireAbility && (
                    <Button
                      secondary
                      transparent
                      onClick={() => setShowSend(true)}
                    >
                      {intl.formatMessage(messages.sendQuestion)}
                    </Button>
                  )}
                  <Button primary onClick={() => setAccessCodeDialog(true)}>
                    {intl.formatMessage(messages.createAccessCode)}
                  </Button>
                </Wrap>
              )}
            </Wrap>

            <Wrap
            grow={'1'}
            overflow={'auto'}
            width={'100%'}
            height={'100%'}
            hiddenXS
          >
            <StyledTable
              data={listGroups.slice(skip, skip + pageSize)}
              onRowClick={(e) => {
                setActiveGroup(e.dataItem);
              }}
              onPageChange={pageHandler}
              scrollable={'virtual'}
              skip={skip}
              pageSize={pageSize}
              total={total}
              rowHeight={55}
              selectedField="checked"
              onSelectionChange={(e) => updateField(e.dataItem, 'checked')}
            >
              <GridColumn
                field={'checked'}
                width="40px"
                headerCell={() => <div />}
              />
              <GridColumn
                field="name"
                width={'calc(100% - 80px - 50%)'}
                title={intl.formatMessage(messages.group)}
                cell={(props) => (
                  <td onClick={() => setActiveGroup(props.dataItem)}>
                    <Wrap fs={'1.4rem'}>{props.dataItem.name}</Wrap>
                  </td>
                )}
              />
              <GridColumn
                field="description"
                width={'calc(100% - 80px - 25%)'}
                title={intl.formatMessage(messages.description)}
                cell={(props) => (
                  <td onClick={() => setActiveGroup(props.dataItem)}>
                    <Wrap fs={'1.4rem'}>
                      {props.dataItem.description || ''}
                    </Wrap>
                  </td>
                )}
              />
              <GridColumn
                width={'40px'}
                cell={(props) => (
                  <StyledTD>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        removeGroup(props.dataItem.id);
                      }}
                    >
                      <DeleteIcon color={red} width={'2rem'} />
                    </Button>
                  </StyledTD>
                )}
              />
            </StyledTable>
          </Wrap>
            <Wrap
              className={'aaa'}
              grow={'1'}
              overflow={'auto'}
              width={'100%'}
              height={'100%'}
              hiddenSM
            >
              <StyledTable
                data={listGroups.slice(skip, skip + pageSize)}
                onRowClick={(e) => {
                  setActiveGroup(e.dataItem);
                }}
                onPageChange={pageHandler}
                scrollable={'virtual'}
                skip={skip}
                pageSize={pageSize}
                total={total}
                rowHeight={55}
                selectedField="checked"
                onSelectionChange={(e) => updateField(e.dataItem, 'checked')}
              >
                <GridColumn
                  field={'checked'}
                  width="40px"
                  headerCell={() => <div />}
                />
                <GridColumn
                  field="name"
                  width={'calc(100% - 80px - 50%)'}
                  title={intl.formatMessage(messages.group)}
                  cell={(props) => (
                    <td onClick={() => setActiveGroup(props.dataItem)}>
                      <Wrap fs={'1.4rem'}>{props.dataItem.name}</Wrap>
                    </td>
                  )}
                />
                <GridColumn
                  width={'40px'}
                  cell={(props) => (
                    <td>
                      <Button
                        icon
                        transparent
                        onClick={(e: any) => {
                          e.preventDefault();
                          removeGroup(props.dataItem.id);
                        }}
                      >
                        <DeleteIcon color={red} width={'2rem'} />
                      </Button>
                    </td>
                  )}
                />
              </StyledTable>
            </Wrap>
          </Wrap>
        </Wrap>
        <SlidingRight className={activeGroup ? 'active' : ''}>
          {activeGroup &&
          <GroupDetails onDetailsHide={()=>setActiveGroup()}
            group={activeGroup}
            showGroupDetails={(g) => {
              setEditedGroup(g);
              setShowGroupDetails(true);
            }}
            profile={profile}
            subscriptionPlan={currentPlan?.subscriptionPlan}
          />
          }
        </SlidingRight>
      </Wrap>
      {showGroupDetails && (
        <DialogWrap
          title={
            editedGroup?.id
              ? intl.formatMessage(messages.editGroup)
              : intl.formatMessage(messages.addGroup)
          }
          children={
            <AddEditGroup
              group={editedGroup}
              onCancel={() => {
                setShowGroupDetails(false);
              }}
              onSave={handleAddEditGroup}
            />
          }
        />
      )}
      {accessCodeDialog && (
        <DialogWrap
          children={
            <AccessCode
              onCancel={() => {
                setAccessCodeDialog(false);
                setListCodes([]);
              }}
              generateCode={generateCode}
              listCodes={listCodes}
            />
          }
        />
      )}
      {showSend && (
        <DialogWrap
          children={
            <SendQuestionnaires
              onCancel={() => setShowSend(false)}
              onSave={sendQuestionnaires}
            />
          }
        />
      )}
      {showConfirmation && (
        <ConfirmationDialog
          bodyText={intl.formatMessage(messages.groupsLimit)}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={() => setShowConfirmation(false)}
          confirmBtnText={ConfirmationBtnText.OK}
          hideCancelBtn
        />
      )}
    </Wrap>
  );
};

export default connector(Groups);
